/**
 * NetSuite Service
 * Handles NetSuite integration API calls for invoicing, customer, and catalog data
 */

import { makeAuthenticatedRequest, createApiError } from './apiService';
import { API_ENDPOINTS } from '../config/api';

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

export interface NetSuiteRef {
  id?: string;
  refName?: string;
}

export interface Customer {
  id?: string;
  entityId?: string;
  companyName?: string;
  isPerson?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  isInactive?: boolean;
}

/** Minimal shape returned by the search / list endpoints */
export interface CustomerSummary {
  id?: string;
  links?: any[];
  companyName?: string;
  entityId?: string;
  [k: string]: any;
}

export interface InvoiceLineItem {
  line?: number;
  item?: NetSuiteRef;
  description?: string;
  quantity?: number;
  rate?: number;
  amount?: number;
}

export interface Invoice {
  id?: string;
  tranId?: string;
  tranDate?: string;
  entity?: NetSuiteRef;
  status?: NetSuiteRef;
  total?: number;
  amountRemaining?: number;
  currency?: NetSuiteRef;
  memo?: string;
  startDate?: string;
  endDate?: string;
  item?: { items?: InvoiceLineItem[] };
  [k: string]: any;
}

export interface InquiryFeeItem {
  id?: string;
  itemId?: string;
  stateCode?: string;
  stateName?: string;
  lookupKey?: string;
  basePrice?: number;
  className?: string;
  description?: string;
  subtype?: string;
  isInactive?: boolean;
}

export interface CatalogItem {
  id?: string;
  itemId?: string;
  state?: string;
  className?: string;
  [k: string]: any;
}

export interface CreateInvoiceLine {
  item: { id: string };
  quantity: number;
  rate?: number;
  description?: string;
}

export interface CreateInvoiceRequest {
  customForm?: { id: string };
  entity: { id: string };
  department?: { id: string };
  terms?: { id: string };
  startDate: string;
  endDate: string;
  memo?: string;
  item: { items: CreateInvoiceLine[] };
}

// NetSuite payment terms (internal ids). Default to Due on receipt (4).
export const INVOICE_TERMS: { id: string; name: string }[] = [
  { id: '7', name: 'Net 10' },
  { id: '1', name: 'Net 15' },
  { id: '2', name: 'Net 30' },
  { id: '3', name: 'Net 60' },
  { id: '4', name: 'Due on receipt' },
];

/** A NetSuite department (id + name) for the invoice department dropdown. */
export interface NetSuiteDepartment {
  id?: string;
  name?: string;
}

export interface NetSuiteLocation {
  id?: string;
  name?: string;
}

/** A billable catalog item (MVR / NMVTIS / service) with its NetSuite base price. */
export interface BillableItem {
  id?: string;
  name?: string;
  category?: string;
  stateCode?: string;
  price?: number;
}

export interface CreateCustomerRequest {
  isPerson: boolean;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

// --------------------------------------------------------------------------
// Service functions
// --------------------------------------------------------------------------

/**
 * Check NetSuite connectivity
 */
export const checkHealth = async (): Promise<{ reachable: boolean }> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.HEALTH);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Get NetSuite API governance limits
 */
export const getGovernanceLimits = async (): Promise<any> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.GOVERNANCE_LIMITS);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Search customers by name (live NetSuite query)
 */
export const searchCustomers = async (name: string): Promise<CustomerSummary[]> => {
  const url = `${API_ENDPOINTS.NETSUITE.CUSTOMERS_SEARCH}?name=${encodeURIComponent(name)}`;
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  // The endpoint may return a paged result { items: [...] } or a bare array
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Get a single customer by NetSuite internal ID
 */
export const getCustomer = async (id: string): Promise<Customer> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.CUSTOMER_BY_ID(id));

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

export interface CustomerAddress {
  addressee?: string;
  addr1?: string;
  addr2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

/**
 * Get a customer's default billing address (NetSuite sub-resource). Returns null if none.
 */
export const getCustomerAddress = async (id: string): Promise<CustomerAddress | null> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.CUSTOMER_ADDRESS(id));
  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }
  const data = await response.json();
  // Backend returns the addressBook element (or the raw customer). Dig for items defensively.
  const items: any[] = data?.items || data?.addressBook?.items || (Array.isArray(data) ? data : []);
  if (!Array.isArray(items) || items.length === 0) return null;
  const billing = items.find((i) => i?.defaultBilling) || items[0];
  const a = billing?.addressBookAddress || billing || {};
  const country = typeof a.country === 'object' ? (a.country?.refName || a.country?.id) : a.country;
  const addr: CustomerAddress = {
    addressee: a.addressee, addr1: a.addr1, addr2: a.addr2,
    city: a.city, state: a.state, zip: a.zip, country,
  };
  // Treat as null if nothing meaningful came back.
  return (addr.addr1 || addr.city || addr.state || addr.zip) ? addr : null;
};

/**
 * Get all customers (for client-side searchable dropdown)
 */
export const getAllCustomers = async (): Promise<CustomerSummary[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.CUSTOMERS_ALL);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  const rows: any[] = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : []);
  // The customers/all SuiteQL endpoint returns lowercase field names (id, entityid,
  // companyname) — unlike getCustomer which returns camelCase. Normalize so the UI can
  // show the company name instead of just the id.
  return rows.map((r) => ({
    ...r,
    id: r.id,
    companyName: r.companyName ?? r.companyname,
    entityId: r.entityId ?? r.entityid,
  }));
};

/**
 * Create a new NetSuite customer
 */
export const createCustomer = async (req: CreateCustomerRequest): Promise<Customer> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.CUSTOMERS, {
    method: 'POST',
    body: JSON.stringify(req),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Get a single invoice by NetSuite internal ID
 */
export const getInvoice = async (id: string): Promise<Invoice> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.INVOICE_BY_ID(id));

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return response.json();
};

/**
 * Get invoices for a customer
 */
export const getInvoicesByCustomer = async (customerId: string): Promise<Invoice[]> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.NETSUITE.INVOICES_BY_CUSTOMER(customerId)
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Create a new invoice in NetSuite
 */
export const createInvoice = async (req: CreateInvoiceRequest): Promise<any> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.INVOICES, {
    method: 'POST',
    body: JSON.stringify(req),
  });

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  // May return JSON body or just a Location header; handle both
  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const location = response.headers.get('Location') || '';
  // Extract the id from the Location URL (last path segment)
  const parts = location.split('/');
  const id = parts[parts.length - 1] || null;
  return { ok: true, location, id };
};

/**
 * Get the inquiry fee catalog (state-level pricing items)
 */
export const getInquiryFees = async (): Promise<InquiryFeeItem[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.INQUIRY_FEES);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Get NetSuite departments for the invoice department dropdown.
 */
export const getDepartments = async (): Promise<NetSuiteDepartment[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.DEPARTMENTS);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  const rows: any[] = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : []);
  // SuiteQL returns lowercase field names (id, name).
  return rows.map((r) => ({ id: r.id ?? r.Id, name: r.name ?? r.Name }));
};

export const getLocations = async (): Promise<NetSuiteLocation[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.LOCATIONS);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  const rows: any[] = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : []);
  return rows.map((r) => ({ id: r.id ?? r.Id, name: r.name ?? r.Name }));
};

/**
 * Get the combined billable-items catalog (MVR + NMVTIS + service items) with base prices.
 * This is the source for the invoice line-item picker.
 */
export const getBillableItems = async (): Promise<BillableItem[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.BILLABLE_ITEMS);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Returns the AAS client price for a given service type / state for the AAS client linked
 * to a NetSuite customer ID. Returns null if no AAS client is linked.
 */
export const getAasPriceForNsCustomer = async (
  nsCustomerId: string,
  serviceType: string,
  stateCode?: string
): Promise<number | null> => {
  const url = API_ENDPOINTS.NETSUITE.CUSTOMER_AAS_PRICE(nsCustomerId, serviceType, stateCode);
  const response = await makeAuthenticatedRequest(url);

  if (!response.ok) return null;

  const data = await response.json();
  if (!data.found || data.price == null) return null;
  return data.price as number;
};

/**
 * Get MVR catalog items
 */
export const getMvrItems = async (): Promise<CatalogItem[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.ITEMS_MVR);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Get NMVTIS catalog items
 */
export const getNmvtisItems = async (): Promise<CatalogItem[]> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.NETSUITE.ITEMS_NMVTIS);

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
};

/**
 * Get governance / API limit information
 */
export { getGovernanceLimits as getNetSuiteGovernanceLimits };
